import React, { useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { requireAuth, useAuth } from "utilities/auth";
import { isInternal } from "utilities/userType";
import { usePends, usePendCategories } from "hooks/crm/usePends";
import { useTickets, useTicketRequestTypes } from "hooks/crm/useTickets";
import PageHeader from "components/PageLayout";
import PageKpis from "components/PageKpis";
import RecordTable from "components/Records/RecordTable";
import RelativeTime from "components/RelativeTime";
import UserAvatar from "components/UserAvatar";
import TicketStatusPill from "components/Tickets/TicketStatusPill";
import { ticketRef } from "utilities/ticketNumber";
import {
  groupPendsForFocus,
  partitionByApplicationStatus,
  pendAgeDays,
  pendAgeTone,
  describeGroup,
} from "utilities/pendFocus";
import { AlertTriangle, ArrowLeft, Inbox, Layers, ListChecks } from "lucide-react";

/**
 * SOUND-690 — the Focus board.
 *
 * CPO framing: "Tickets in aggregate are helpful for keeping individuals aware of
 * their own work, but at a team level it's tough to have a discussion around what
 * is a priority across the team specifically related to our primary sales
 * pipeline."
 *
 * So this is NOT a priority queue and deliberately has no assignee or SLA. It is
 * a snapshot of the application funnel's live work, in the two shapes the
 * merchant-services standup actually walks: the tickets that open a day, and
 * every outstanding ask, grouped so duplicated ones collapse into one conversation.
 *
 * Its own route (/mid-applications/focus) rather than a query param, so a standup
 * link is a link.
 */

function GroupHeader({ group }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 border-b border-border/60 bg-muted/30 px-4 py-2.5">
      <div className="flex items-baseline gap-2 min-w-0">
        <span className="truncate font-medium">{group.clientName || "(unknown client)"}</span>
        <span className="truncate text-xs text-muted-foreground">{describeGroup(group)}</span>
      </div>
      {/* The actionable signal: the same ask outstanding at more than one
          processor is collected once and cleared several times. Only shown when
          it is TRUE — a badge that is always there stops being read. */}
      {group.spansProcessors && group.repeatedCategories.length > 0 && (
        <span className="badge badge-brand shrink-0" title="The same ask is outstanding at more than one processor — collecting it once clears both">
          <Layers className="h-3 w-3" />
          collect once
        </span>
      )}
    </div>
  );
}

function FocusInner() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const internal = isInternal(user);
  const [showClosed, setShowClosed] = useState(false);

  // Every open pend the caller may see. The board groups client-side because
  // grouping is a VIEW decision — the same rows sorted by age are a different,
  // equally valid board, and paging a grouped list server-side would cut groups
  // in half across page boundaries.
  const pendFilters = useMemo(() => ({ state: "open" }), []);
  const { pends, summary, loading, error, refetch } = usePends({ filters: pendFilters, limit: 200 });
  const { categories } = usePendCategories();

  // WHICH request types belong here is the API's answer, not this component's.
  //
  // It was a hardcoded array — a list living in the view. Renaming a key in
  // config/ticketRequestTypes.mjs silently emptied the board with nothing
  // failing, and changing the set meant a frontend deploy. It is a `focus: true`
  // flag on the request type now, so the workflow owner can put a new type on the
  // team's morning board without shipping any code here.
  const { requestTypes } = useTicketRequestTypes();
  const focusKeys = useMemo(
    () => requestTypes.filter((t) => t.focus).map((t) => t.key),
    [requestTypes]
  );

  // Filtered SERVER-side, not with a .filter() over a page.
  //
  // The board previously fetched 50 open tickets and narrowed them here, and
  // showed nothing: the three active new_application tickets rank #152-154 of
  // 227 active tickets by -updatedAt, so the page never contained them. Raising
  // the limit would only move the cliff — the count has to be the server's.
  //
  // `requestType: ""` while the vocabulary is still loading would drop the filter
  // and fetch EVERY open ticket, so the request waits for the keys.
  const ticketFilters = useMemo(
    () => ({ state: "open", requestType: focusKeys.join(",") }),
    [focusKeys]
  );
  const { tickets: focusTickets, pagination: ticketPagination, loading: ticketsLoading, error: ticketsError } =
    useTickets({ filters: ticketFilters, limit: 50, skip: focusKeys.length === 0 });

  const catLabel = useMemo(() => {
    const map = Object.fromEntries(categories.map((c) => [c.key, c.label]));
    return (key) => map[key] || null;
  }, [categories]);

  const { live, closed } = useMemo(() => partitionByApplicationStatus(pends), [pends]);
  const groups = useMemo(() => groupPendsForFocus(showClosed ? pends : live), [pends, live, showClosed]);

  const pendColumns = useMemo(
    () => [
      {
        key: "processorName",
        header: "Processor",
        width: "14%",
        cell: (p) => <span className="truncate">{p.processorName || "—"}</span>,
      },
      {
        key: "category",
        header: "Category",
        width: "16%",
        cell: (p) => {
          // A legacy pend carries a null category until the composer is updated
          // (SOUND-621). Render its original words rather than a blank — the
          // operator wrote something real.
          const label = catLabel(p.category);
          return label ? (
            <span className="badge badge-muted">{label}</span>
          ) : (
            <span className="text-xs italic text-muted-foreground">{p.type || "—"}</span>
          );
        },
      },
      {
        key: "detail",
        header: "Ask",
        width: "46%",
        truncate: false,
        cell: (p) => (
          <div className="min-w-0">
            {p.detail && <div className="truncate text-xs font-medium">{p.detail}</div>}
            <div className="line-clamp-2 text-xs text-muted-foreground">{p.description}</div>
          </div>
        ),
      },
      {
        key: "age",
        header: "Age",
        width: "12%",
        cell: (p) => {
          const days = pendAgeDays(p);
          const tone = pendAgeTone(days);
          return (
            <span className={`badge badge-${tone === "muted" ? "muted" : tone}`}>
              {days == null ? "—" : `${days}d`}
            </span>
          );
        },
      },
      {
        key: "createdBy",
        header: "Raised by",
        width: "12%",
        headerClassName: "hidden lg:table-cell",
        className: "hidden lg:table-cell",
        cell: (p) =>
          p.createdBy?.name ? (
            <UserAvatar user={{ name: p.createdBy.name, email: p.createdBy.email }} size={22} />
          ) : (
            // 51 of 161 pends have no recorded author — a real gap, shown as one.
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
    ],
    [catLabel]
  );

  const ticketColumns = useMemo(
    () => [
      {
        key: "number",
        header: "#",
        width: "10%",
        className: "font-mono text-[11px] tabular-nums text-muted-foreground",
        cell: (t) => <>{ticketRef(t.number)}</>,
      },
      {
        key: "client",
        header: "Client",
        width: "22%",
        cell: (t) => <span className="truncate">{t.companyName || "—"}</span>,
      },
      { key: "title", header: "Title", width: "40%", cell: (t) => <span className="truncate">{t.title}</span> },
      {
        key: "status",
        header: "Status",
        width: "14%",
        cell: (t) => <TicketStatusPill status={t.status} statusCategory={t.statusCategory} />,
      },
      {
        key: "updatedAt",
        header: "Updated",
        width: "14%",
        className: "text-muted-foreground",
        cell: (t) => <RelativeTime value={t.updatedAt} />,
      },
    ],
    []
  );

  if (!internal) {
    return (
      <div className="container py-10 text-sm text-muted-foreground">
        The Focus board is an internal view.
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Focus"
        subtitle="The application funnel's live work — what is open, and what can be cleared together."
        actions={
          <Link to="/mid-applications" className="btn btn-outline">
            <ArrowLeft className="h-4 w-4" />
            All applications
          </Link>
        }
      />

      <div className="container space-y-6 py-6">
        <PageKpis
          items={[
            { label: "Open pends", value: summary.open ?? 0, tone: "warning", loading, icon: <AlertTriangle /> },
            { label: "On live applications", value: live.length, tone: "brand", loading, icon: <ListChecks /> },
            { label: "Client groups", value: groups.length, tone: "info", loading, icon: <Layers /> },
            {
              // The SERVER's total, never focusTickets.length — that is the page.
              label: "Application tickets",
              value: ticketPagination.total ?? 0,
              tone: "info",
              loading: ticketsLoading,
              icon: <Inbox />,
            },
          ]}
        />

        {/* ---- Action items ---- */}
        <section className="space-y-2">
          <h2 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            <Inbox className="h-3.5 w-3.5" />
            Action items
          </h2>
          <RecordTable
            columns={ticketColumns}
            rows={focusTickets}
            rowKey={(t) => t.ticketId}
            onRowClick={(t) => navigate(`/tickets/${t.ticketId}`)}
            loading={ticketsLoading}
            error={ticketsError}
            empty={{
              icon: Inbox,
              heading: "No open application tickets",
              description: "New-application and status-check tickets appear here.",
            }}
          />
        </section>

        {/* ---- Open pends, grouped ---- */}
        <section className="space-y-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <AlertTriangle className="h-3.5 w-3.5" />
              Open pends
            </h2>
            {/* The scope is STATED, never silent. Hiding rows without saying so
                reads as "covered everything" when it did not. */}
            {closed.length > 0 && (
              <button type="button" className="btn btn-ghost text-xs" onClick={() => setShowClosed((v) => !v)}>
                {showClosed
                  ? `Hide ${closed.length} on closed applications`
                  : `${closed.length} hidden on closed applications`}
              </button>
            )}
          </div>

          {error && (
            <div className="surface p-4 text-sm text-destructive">
              Failed to load pends.{" "}
              <button className="underline" onClick={refetch}>
                Retry
              </button>
            </div>
          )}

          {!error && !loading && groups.length === 0 && (
            <div className="surface p-8 text-center text-sm text-muted-foreground">
              Nothing outstanding. {closed.length > 0 && `${closed.length} pend(s) sit on closed applications.`}
            </div>
          )}

          {groups.map((group) => (
            <div key={group.key} className="surface overflow-hidden">
              <GroupHeader group={group} />
              <RecordTable
                columns={pendColumns}
                rows={group.pends}
                rowKey={(p) => p.pendId}
                onRowClick={(p) => navigate(`/mid-applications/${p.applicationId}`)}
                loading={false}
              />
            </div>
          ))}
        </section>
      </div>
    </div>
  );
}

export default requireAuth(FocusInner);
